//
// # Wire
//

// Local Imports
import { schema } from "GenAlphaSchema";
import { Point } from "../core";
import { Path, PathStyle, type Text } from "../graphics";

export type WireSegment = schema.schematic.WireSegment;

// The canonical location for a wire label: the midpoint of the wire's two
// endpoints. A label's `loc` is an absolute schematic coordinate, so it does
// not track the wire on its own; this is the single source of truth for where
// a label belongs relative to the wire's geometry. Used both when a label is
// first added (`Schematic.updateWireLabel`) and whenever a wire's endpoints
// change (move / slide / stretch / drag-reroute / paste) so the label ends up
// exactly where a freshly-added label would — i.e. the same position it would
// have if the wire were deleted and re-drawn at the new location.
export function wireLabelLoc(start: Point, end: Point): Point {
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
}

// Recompute every label's `loc` for a wire's new endpoints, returning a fresh
// labels array (new objects, new `loc`s) so callers can use it as change-log
// `from`/`to` data without aliasing the originals. All other label fields
// (text, height, alignment, baseline, rotation, id) are preserved.
export function recenterLabels(
  labels: Array<Text>,
  start: Point,
  end: Point
): Array<Text> {
  const loc = wireLabelLoc(start, end);
  return labels.map((label) => ({ ...label, loc: Point.clone(loc) }));
}

// Deep-clone a wire's label array (new objects, cloned `loc`s) so a captured
// "original" snapshot does not alias the live wire data. Used by move modes to
// hold pre-move labels for change-log `from` data and abort/restore.
export function cloneWireLabels(labels: Array<Text>): Array<Text> {
  return labels.map((label) => ({ ...label, loc: Point.clone(label.loc) }));
}

export const WireSegment = {
  new: (endpoints: [Point, Point]): WireSegment => {
    return { endpoints, style: defaultPathStyle(), labels: [] };
  },
  // Convert our two `endpoints` to the point-array of a `graphics.Path`.
  toPath: (wireSegment: WireSegment): Path => {
    return Path.new({
      points: wireSegment.endpoints,
      style: wireSegment.style,
    });
  },
};

function defaultPathStyle(): PathStyle {
  return {
    ...PathStyle.default(),
    width: 2,
  };
}
